import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MultiSelect } from "../components/MultiSelect";
import { INDUSTRIES, PRODUCT_TYPES, TIERS, STATES } from "../constants/lenderOptions";
import { ArrowLeft, GitPullRequest } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = {
  name: "",
  tier: "", tiers: [],
  contact_name: "", contact_email: "", contact_phone: "",
  industries_funded: [], preferred_industries: [], restricted_industries: [],
  min_term_months: "", max_term_months: "",
  min_funding: "", max_funding: "", preferred_funding: "",
  commission_structure: "", commission_points: "", fees: "", buy_sell_rate_notes: "",
  max_negative_days: "", max_nsfs: "",
  min_monthly_revenue: "", min_avg_daily_balance: "",
  min_time_in_business_months: "", min_credit_score: "",
  states_funded: [], states_not_funded: [],
  product_types: [], required_documents: [],
  speed_to_fund: "", renewal_policy: "", stacking_policy: "",
  preferred_deal_profile: "", red_flags: "", notes: "",
  status: "active",
};

const TIERS_LOCAL = TIERS;

const numericFields = [
  "min_term_months","max_term_months","min_funding","max_funding","preferred_funding",
  "max_negative_days","max_nsfs","min_monthly_revenue","min_avg_daily_balance",
  "min_time_in_business_months","min_credit_score",
];
const listFields = ["industries_funded","preferred_industries","restricted_industries","states_funded","states_not_funded","product_types","required_documents"];

const parseList = (v) => (Array.isArray(v) ? v : String(v || "").split(",").map((x) => x.trim()).filter(Boolean));

export default function LenderForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isLeader = user?.role === "leader";
  const isPrivileged = isAdmin || isLeader;  // bypasses approval workflow
  const [form, setForm] = useState(empty);
  const [original, setOriginal] = useState(null);  // snapshot for diffing on edits
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/lenders/${id}`).then(({ data }) => {
      const tiers = Array.isArray(data.tiers) && data.tiers.length ? data.tiers : (data.tier ? [data.tier] : []);
      const merged = { ...empty, ...data, tiers };
      setForm(merged);
      setOriginal(merged);
    }).catch(() => toast.error("Failed to load lender"));
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = () => {
    const payload = { ...form };
    numericFields.forEach((k) => { payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]); });
    listFields.forEach((k) => { payload[k] = parseList(payload[k]); });
    return payload;
  };

  const diffChanges = (payload) => {
    if (!original) return payload;
    const out = {};
    for (const k of Object.keys(payload)) {
      const a = original[k];
      const b = payload[k];
      const same = Array.isArray(a) && Array.isArray(b)
        ? a.length === b.length && a.every((x, i) => x === b[i])
        : a === b;
      if (!same) out[k] = b;
    }
    return out;
  };

  const submitProposal = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = buildPayload();
      const changes = diffChanges(payload);
      if (!Object.keys(changes).length) {
        toast.info("No changes to propose");
        setBusy(false);
        return;
      }
      const { data } = await api.post(`/lenders/${id}/propose-edit`, { changes, reason });
      toast.success(`Proposal submitted — needs 1 teammate to approve (${Object.keys(changes).length} fields)`);
      nav(`/proposals`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit proposal");
    } finally { setBusy(false); }
  };

  const submitDirect = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.put(`/lenders/${id}`, payload);
        toast.success("Lender updated (admin override — no approval required)");
        nav(`/lenders/${id}`);
      } else {
        const { data } = await api.post(`/lenders`, payload);
        toast.success("Lender created");
        nav(`/lenders/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  // Default submit handler:
  // - For NEW lender (admin-only): create directly
  // - For EDITS: admin & leader can save directly (no approval). Employees go through proposal flow.
  const submit = isEdit ? (isPrivileged ? submitDirect : submitProposal) : submitDirect;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="text-sm text-slate-500">
        <Link to="/lenders" className="hover:text-slate-800 inline-flex items-center gap-1"><ArrowLeft size={14} /> Lender directory</Link>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">{isEdit ? "Propose edit" : "New lender"}</div>
        <h1 className="font-heading text-4xl font-bold tracking-tighter">{isEdit ? form.name || "Lender" : "Add a new lender"}</h1>
        {isEdit && (
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            {isPrivileged ? (
              <>As a {isAdmin ? "Admin" : "Leader"}, your edits apply <strong>immediately</strong> — no approval required.</>
            ) : (
              <>Any teammate can propose changes. Your edit will go live <strong>after one other person approves</strong> it on the
              <Link to="/proposals" className="text-blue-600 hover:underline ml-1">Pending edits</Link> page.</>
            )}
          </p>
        )}
      </div>

      <form onSubmit={submit} className="space-y-8">
        <Section title="Identity & contact">
          <Grid>
            <FormField label="Lender name" required><Input required value={form.name} onChange={(e) => set("name", e.target.value)} /></FormField>
            <FormField label="Tier(s) — pick one or more">
              <MultiSelect
                options={TIERS_LOCAL}
                value={form.tiers || []}
                onChange={(arr) => setForm((f) => ({ ...f, tiers: arr, tier: arr[0] || "" }))}
                testId="lender-form-tiers"
                placeholder="—"
              />
            </FormField>
            <FormField label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="preferred">Preferred</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="test">Test</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Contact name"><Input value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></FormField>
            <FormField label="Contact email"><Input type="email" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></FormField>
            <FormField label="Contact phone"><Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></FormField>
            <FormField label="Speed to fund"><Input value={form.speed_to_fund || ""} onChange={(e) => set("speed_to_fund", e.target.value)} placeholder="e.g. 24–48 hours" /></FormField>
          </Grid>
        </Section>

        <Section title="★ Key criteria">
          <Grid>
            <FormField label="Max term (months)"><Input type="number" value={form.max_term_months} onChange={(e) => set("max_term_months", e.target.value)} placeholder="e.g. 18" data-testid="key-max-term" /></FormField>
            <FormField label="Min FICO score"><Input type="number" value={form.min_credit_score} onChange={(e) => set("min_credit_score", e.target.value)} placeholder="e.g. 550" data-testid="key-min-fico" /></FormField>
            <FormField label="Min time in business (months)"><Input type="number" value={form.min_time_in_business_months} onChange={(e) => set("min_time_in_business_months", e.target.value)} placeholder="e.g. 12" data-testid="key-min-tib" /></FormField>
            <FormField label="Rates / buy & sell" full><Textarea rows={2} value={form.buy_sell_rate_notes || ""} onChange={(e) => set("buy_sell_rate_notes", e.target.value)} placeholder="e.g. Buy 1.28 / Sell 1.42–1.49" data-testid="key-rates" /></FormField>
            <FormField label="Commission points"><Input value={form.commission_points || ""} onChange={(e) => set("commission_points", e.target.value)} placeholder="e.g. 10–12 pts" data-testid="key-commission-points" /></FormField>
            <FormField label="Fees"><Input value={form.fees || ""} onChange={(e) => set("fees", e.target.value)} placeholder="e.g. $395 origination + $50 ACH" data-testid="key-fees" /></FormField>
            <FormField label="Preferred industries" full>
              <MultiSelect
                options={INDUSTRIES}
                value={Array.isArray(form.preferred_industries) ? form.preferred_industries : (form.preferred_industries ? String(form.preferred_industries).split(",").map(s => s.trim()).filter(Boolean) : [])}
                onChange={(v) => set("preferred_industries", v)}
                placeholder="Pick preferred industries…"
                searchable
                testId="key-preferred-industries"
              />
            </FormField>
            <FormField label="Restricted / prohibited industries" full>
              <MultiSelect
                options={INDUSTRIES}
                value={Array.isArray(form.restricted_industries) ? form.restricted_industries : (form.restricted_industries ? String(form.restricted_industries).split(",").map(s => s.trim()).filter(Boolean) : [])}
                onChange={(v) => set("restricted_industries", v)}
                placeholder="Pick restricted industries…"
                searchable
                testId="key-restricted-industries"
              />
            </FormField>
          </Grid>
        </Section>

        <Section title="Funding & term">
          <Grid>
            <FormField label="Min funding ($)"><Input type="number" value={form.min_funding} onChange={(e) => set("min_funding", e.target.value)} /></FormField>
            <FormField label="Max funding ($)"><Input type="number" value={form.max_funding} onChange={(e) => set("max_funding", e.target.value)} /></FormField>
            <FormField label="Preferred funding ($)"><Input type="number" value={form.preferred_funding} onChange={(e) => set("preferred_funding", e.target.value)} /></FormField>
            <FormField label="Min term (months)"><Input type="number" value={form.min_term_months} onChange={(e) => set("min_term_months", e.target.value)} /></FormField>
            <FormField label="Industries funded" full>
              <MultiSelect
                options={INDUSTRIES}
                value={Array.isArray(form.industries_funded) ? form.industries_funded : (form.industries_funded ? String(form.industries_funded).split(",").map(s => s.trim()).filter(Boolean) : [])}
                onChange={(v) => set("industries_funded", v)}
                placeholder="Pick industries this lender funds…"
                searchable
              />
            </FormField>
            <FormField label="Product types" full>
              <MultiSelect
                options={PRODUCT_TYPES}
                value={Array.isArray(form.product_types) ? form.product_types : (form.product_types ? String(form.product_types).split(",").map(s => s.trim()).filter(Boolean) : [])}
                onChange={(v) => set("product_types", v)}
                placeholder="Pick product types…"
              />
            </FormField>
          </Grid>
        </Section>

        <Section title="Commission (admin / authorized only)">
          <Grid>
            <FormField label="Commission structure" full><Input value={form.commission_structure || ""} onChange={(e) => set("commission_structure", e.target.value)} placeholder="e.g. 12% upfront on funded amount" /></FormField>
          </Grid>
        </Section>

        <Section title="Underwriting (additional)">
          <Grid>
            <FormField label="Max negative days"><Input type="number" value={form.max_negative_days} onChange={(e) => set("max_negative_days", e.target.value)} /></FormField>
            <FormField label="Max NSFs"><Input type="number" value={form.max_nsfs} onChange={(e) => set("max_nsfs", e.target.value)} /></FormField>
            <FormField label="Min monthly revenue ($)"><Input type="number" value={form.min_monthly_revenue} onChange={(e) => set("min_monthly_revenue", e.target.value)} /></FormField>
            <FormField label="Min avg daily balance ($)"><Input type="number" value={form.min_avg_daily_balance} onChange={(e) => set("min_avg_daily_balance", e.target.value)} /></FormField>
          </Grid>
        </Section>

        <Section title="Geography & docs">
          <Grid>
            <FormField label="States funded" full>
              <MultiSelect
                options={STATES}
                value={Array.isArray(form.states_funded) ? form.states_funded : (form.states_funded ? String(form.states_funded).split(",").map(s => s.trim()).filter(Boolean) : [])}
                onChange={(v) => set("states_funded", v)}
                placeholder="Pick states this lender funds…"
                searchable
              />
            </FormField>
            <FormField label="States NOT funded" full>
              <MultiSelect
                options={STATES}
                value={Array.isArray(form.states_not_funded) ? form.states_not_funded : (form.states_not_funded ? String(form.states_not_funded).split(",").map(s => s.trim()).filter(Boolean) : [])}
                onChange={(v) => set("states_not_funded", v)}
                placeholder="Pick states this lender excludes…"
                searchable
              />
            </FormField>
            <FormField label="Required documents (comma)" full><Input value={Array.isArray(form.required_documents) ? form.required_documents.join(", ") : form.required_documents} onChange={(e) => set("required_documents", e.target.value)} placeholder="3 months bank statements, ID, Voided check…" /></FormField>
          </Grid>
        </Section>

        <Section title="Policies & profile">
          <Grid>
            <FormField label="Renewal policy"><Input value={form.renewal_policy || ""} onChange={(e) => set("renewal_policy", e.target.value)} /></FormField>
            <FormField label="Stacking policy"><Input value={form.stacking_policy || ""} onChange={(e) => set("stacking_policy", e.target.value)} /></FormField>
            <FormField label="Preferred deal profile" full><Textarea value={form.preferred_deal_profile || ""} onChange={(e) => set("preferred_deal_profile", e.target.value)} /></FormField>
            <FormField label="Red flags" full><Textarea value={form.red_flags || ""} onChange={(e) => set("red_flags", e.target.value)} /></FormField>
            <FormField label="Internal notes" full><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></FormField>
          </Grid>
        </Section>

        {isEdit && !isPrivileged && (
          <Section title="Why this edit (optional)">
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Briefly tell the approver what changed and why (e.g., 'Got updated rate sheet from John on 1/15')"
              data-testid="proposal-reason"
            />
          </Section>
        )}

        <div className="flex justify-end gap-3 flex-wrap">
          <Button type="button" variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
          {isEdit && isPrivileged && (
            <Button type="button" disabled={busy} variant="outline" onClick={submitProposal} data-testid="lender-form-propose" title="Submit as a proposal that needs approval (peer review)">
              <GitPullRequest size={14} weight="bold" className="mr-1.5" /> Submit as proposal instead
            </Button>
          )}
          <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={TID.lenderFormSubmit}>
            {busy ? "Saving…" : (
              isEdit
                ? (isPrivileged ? "Update lender" : (<><GitPullRequest size={14} weight="bold" className="mr-1.5" /> Submit for approval</>))
                : "Create lender"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6">
    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-4">{title}</div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;
const FormField = ({ label, children, required, full }) => (
  <div className={full ? "md:col-span-3" : ""}>
    <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    <div className="mt-1">{children}</div>
  </div>
);
